/**
 * Renders an image primitive node.
 *
 * The image source can be a static URL (node.src) or resolved from a binding.
 */

import { Box } from "@mui/material";
import React from "react";
import { resolveStyles } from "../layout-types";
import type { PrimitiveNode, InjectedData } from "../layout-types";
import { useProtectedMediaSrc } from "../../layout-compiler/useProtectedMediaSrc";

interface LayoutImageProps {
  node: PrimitiveNode;
  data: InjectedData;
}

const toCssLength = (value: unknown): string | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return `${Math.max(0, value)}px`;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return value.trim();
  }
  return undefined;
};

const LayoutImage: React.FC<LayoutImageProps> = ({ node, data }) => {
  const resolved = resolveStyles(node.style, data);

  const src = useProtectedMediaSrc(node.src);

  const isHidden = resolved.visibility === false;
  if (isHidden) return null;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        opacity: resolved.opacity as number | undefined,
        borderRadius: toCssLength(resolved.borderRadius),
        borderTopLeftRadius: toCssLength(resolved.borderTopLeftRadius),
        borderTopRightRadius: toCssLength(resolved.borderTopRightRadius),
        borderBottomRightRadius: toCssLength(resolved.borderBottomRightRadius),
        borderBottomLeftRadius: toCssLength(resolved.borderBottomLeftRadius),
        border: resolved.border as string | undefined,
        borderTop: resolved.borderTop as string | undefined,
        borderRight: resolved.borderRight as string | undefined,
        borderBottom: resolved.borderBottom as string | undefined,
        borderLeft: resolved.borderLeft as string | undefined,
        boxSizing: "border-box",
      }}
    >
      {src ? (
        <img
          src={src}
          alt={node.label ?? ""}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundColor: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#999",
            fontSize: 12,
          }}
        >
          {node.label ?? "Image"}
        </Box>
      )}
    </Box>
  );
};

export default LayoutImage;
